import { api } from './api';

export interface ArticleMetric {
  site: string;
  wp_post_id: string;
  url: string | null;
  window: string;
  pageviews: number;
  sessions: number;
  engaged_sessions: number;
  avg_engagement_time: number;
  engagement_rate: number;
  gsc_impressions: number;
  gsc_clicks: number;
  gsc_ctr: number;
  gsc_position: number | null;
  collected_at: string;
}

export interface MetricsOverview {
  site: string | null;
  window: string;
  articles: number;
  pageviews: number;
  sessions: number;
  avg_engagement_rate: number;
  avg_engagement_time: number;
  gsc_clicks: number;
  gsc_impressions: number;
  gsc_ctr: number;
  gsc_avg_position: number | null;
  position_buckets: { label: string; count: number }[];
  top_articles: {
    wp_post_id: string;
    url: string | null;
    pageviews: number;
    gsc_clicks: number;
    gsc_position: number | null;
  }[];
  top_queries: {
    query: string;
    wp_post_id: string;
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
  }[];
}

export interface DailyPoint {
  date: string;
  pageviews: number;
  sessions: number;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number | null;
}

export interface RealtimeData {
  active_users: number;
  top_pages: { name: string; active_users: number; views: number }[];
  /** Leitores em página de erro AGORA — separados para não se disfarçarem de matéria. */
  broken_users?: number;
  by_minute: { minutes_ago: number; active_users: number }[];
  configured: boolean;
}

export interface RundownSignal {
  id: string;
  signal_type: string;
  severity: 'high' | 'medium' | 'low';
  score: number;
  ref_key: string;
}
export interface RundownRow {
  wp_post_id: string;
  url: string | null;
  title: string | null;
  thumbnail: string | null;
  editoria: string | null;
  metodo: string | null;
  fonte: string | null;
  pageviews: number;
  sessions: number;
  engagement_rate: number;
  avg_engagement_time: number;
  gsc_clicks: number;
  gsc_impressions: number;
  gsc_ctr: number;
  gsc_position: number | null;
  health: number;
  level: 'ok' | 'atencao' | 'critico';
  origem_sistema: boolean;
  signals: RundownSignal[];
}
export interface RundownFilters {
  editoria?: string;
  metodo?: string;
  fonte?: string;
  level?: string;
  origem?: string; // sistema | externo
  window?: string; // 7d | 28d | 90d
  q?: string;
  order?: string; // pageviews|clicks|impressions|ctr|tempo|position|problemas
}

export interface SeoAssistResult {
  variations: { title: string; meta: string }[];
  tips: string[];
}

export interface RankingAutor {
  autor: string;
  artigos: number;
  pageviews: number;
  cliques: number;
  impressoes: number;
  ctr: number; // fração (0–1)
  pos_media: number | null;
  tempo_medio: number; // segundos
}

export interface AutorArtigo {
  wp_post_id: string;
  title: string | null;
  url: string | null;
  pageviews: number;
  gsc_clicks: number;
  gsc_ctr: number;
  gsc_position: number | null;
  avg_engagement_time: number;
}

export const metricsApi = {
  /** Correção prévia no editor (antes de publicar): título/meta + dicas a partir do rascunho. */
  seoAssist: (body: { title: string; content: string; keyword?: string }) =>
    api.post<SeoAssistResult>('/metrics/seo-assist', body),
  /** B-27: melhorar TEXTO (presets + instrução livre, sob as travas de data/fidelidade). */
  textAssist: (body: { title: string; content: string; preset?: string; instruction?: string }) =>
    api.post<{ content_html: string; model: string }>('/metrics/text-assist', body),
  /** Rundown de matérias (métricas + sinais + nível), com filtros profundos. */
  rundown: (site: string, f: RundownFilters = {}) => {
    const qs = new URLSearchParams({ site });
    if (f.editoria) qs.set('editoria', f.editoria);
    if (f.metodo) qs.set('metodo', f.metodo);
    if (f.fonte) qs.set('fonte', f.fonte);
    if (f.level) qs.set('level', f.level);
    if (f.origem) qs.set('origem', f.origem);
    if (f.window) qs.set('window', f.window);
    if (f.q) qs.set('q', f.q);
    if (f.order) qs.set('order', f.order);
    return api.get<RundownRow[]>(`/metrics/rundown?${qs.toString()}`);
  },
  /** Valores distintos pros filtros (editoria/método/fonte). */
  facets: (site: string) =>
    api.get<{ editorias: string[]; metodos: string[]; fontes: string[] }>(
      `/metrics/facets?site=${encodeURIComponent(site)}`,
    ),
  /** Tempo real (GA4 Realtime, últimos 30 min) — usuários ativos agora. */
  realtime: (site: string) =>
    api.get<RealtimeData>(`/metrics/realtime?site=${encodeURIComponent(site)}`),
  /** Série diária do site (evolução) para os gráficos. */
  timeseries: (site: string, days = 28) =>
    api.get<DailyPoint[]>(`/metrics/timeseries?site=${encodeURIComponent(site)}&days=${days}`),
  /** Visão geral do site (GA4 + Search Console) para a tela de Analytics. */
  overview: (site?: string, window = '28d') => {
    const qs = new URLSearchParams({ window });
    if (site) qs.set('site', site);
    return api.get<MetricsOverview>(`/metrics/overview?${qs.toString()}`);
  },
  /** Métricas de 1 matéria (null se ainda não houver coleta). */
  article: (wpPostId: string | number, site: string, window = '28d') =>
    api.get<ArticleMetric | null>(
      `/metrics/article/${wpPostId}?site=${encodeURIComponent(site)}&window=${window}`,
    ),
  /** Ranking de matérias por desempenho. */
  top: (params: { site?: string; window?: string; order?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.site) qs.set('site', params.site);
    if (params.window) qs.set('window', params.window);
    if (params.order) qs.set('order', params.order);
    if (params.limit) qs.set('limit', String(params.limit));
    const s = qs.toString();
    return api.get<ArticleMetric[]>(`/metrics/top${s ? `?${s}` : ''}`);
  },
  /** Ranking de autores por audiência (quem produziu trouxe quantas views). */
  rankingAutores: (params: { site?: string; window?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.site) qs.set('site', params.site);
    if (params.window) qs.set('window', params.window);
    if (params.limit) qs.set('limit', String(params.limit));
    const s = qs.toString();
    return api.get<RankingAutor[]>(`/metrics/ranking-autores${s ? `?${s}` : ''}`);
  },
  /** Drill-down: os artigos de um autor com CTR/pageview/tempo de leitura. */
  autorArtigos: (
    autor: string,
    params: { site?: string; window?: string; limit?: number } = {},
  ) => {
    const qs = new URLSearchParams({ autor });
    if (params.site) qs.set('site', params.site);
    if (params.window) qs.set('window', params.window);
    if (params.limit) qs.set('limit', String(params.limit));
    return api.get<AutorArtigo[]>(`/metrics/autor-artigos?${qs.toString()}`);
  },
  /** Dispara o sync agora (puxa GA4/GSC). */
  sync: () => api.post<{ updated: number }>('/metrics/sync', {}),
  /** M9: sentinelas do site — editoria sumida/inchada e arquivo inacessível. */
  /** Calendário de feriados — o que "dia útil" significa nas esteiras. */
  feriados: (calendario: 'nacional' | 'mercado' = 'nacional', ano?: number) => {
    const qs = new URLSearchParams({ calendario });
    if (ano) qs.set('ano', String(ano));
    return api.get<FeriadosResposta>(`/metrics/feriados?${qs.toString()}`);
  },
  /** Trava de concorrente: ligada? quais matérias automáticas ela derrubou no período. */
  travaConcorrente: (dias = 7) =>
    api.get<TravaConcorrenteResposta>(`/metrics/trava-concorrente?dias=${dias}`),
  sentinela: (site: string) =>
    api.get<SentinelaSnapshot>(`/metrics/sentinela?site=${encodeURIComponent(site)}`),
  sentinelaVarrer: (site: string) =>
    api.post<SentinelaResultado>(`/metrics/sentinela/varrer?site=${encodeURIComponent(site)}`, {}),
  /** Estado real das esteiras automáticas + alarmes (janela perdida, fábrica calada). */
  esteiras: () => api.get<EsteirasVisao>('/metrics/esteiras'),
};

/** Um alarme de esteira automática (job que falhou, janela perdida, fábrica calada). */
export interface EsteiraAlarme {
  tipo: 'janela_perdida' | 'retry' | 'erro' | 'fabrica_desligada' | 'fabrica_calada' | string;
  gravidade: 'alta' | 'media' | string;
  job: string;
  detalhe: string;
}

export interface EsteiraJob {
  job: string;
  ultima_rodada: string | null;
  resultado: string;
  tentativas: number;
}

export interface EsteirasVisao {
  esteiras: EsteiraJob[];
  alarmes: EsteiraAlarme[];
  em: string;
}

/** M9 — um alarme das sentinelas. */
export interface SentinelaAlarme {
  tipo: 'arquivo' | 'inchaco' | 'sumiu' | string;
  gravidade: 'alta' | 'media' | string;
  slug: string;
  posts: number;
  detalhe: string;
}

export interface SentinelaResultado {
  site: string;
  categorias?: number;
  alarmes?: SentinelaAlarme[];
  em?: string;
  erro?: string;
}

export interface SentinelaSnapshot {
  em?: string;
  alarmes?: SentinelaAlarme[];
}

/** Um feriado do calendário (nacional, B3 ou local do cliente). */
export interface FeriadoItem {
  data: string;
  nome: string;
  origem: 'nacional' | 'mercado' | 'local' | string;
  dia_semana: number;
}

export interface TravaConcorrenteResposta {
  ligada: boolean;
  /** lista em uso: a gravada no painel ou, se nunca gravaram, a padrão */
  marcas: string[];
  dias: number;
  quedas: number;
  ultimas: {
    em: string | null;
    titulo: string | null;
    marca: string | null;
    onde: string | null;
  }[];
}

export interface FeriadosResposta {
  ano: number;
  calendario: string;
  hoje: string;
  hoje_e_feriado: boolean;
  hoje_e_util: boolean;
  proximo_dia_util: string;
  itens: FeriadoItem[];
}
